import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { Tooltip } from './Tooltip';

/**
 * ActionIconButton Component
 *
 * Reusable icon-only button with tooltip for table actions.
 * Consistent sizing and styling across the application.
 *
 * @param {Object} props
 * @param {string} props.icon - Iconify icon name (e.g., 'lucide:eye')
 * @param {Function} props.onClick - Click callback
 * @param {string} props.tooltip - Tooltip text
 * @param {string} props.variant - Color variant: 'default', 'primary', 'danger' (default: 'default')
 * @param {string} props.ariaLabel - Accessibility label
 * @param {boolean} props.disabled - Whether the button is disabled
 */
export function ActionIconButton({
  icon,
  onClick,
  tooltip,
  variant = 'default',
  ariaLabel,
  disabled = false,
}) {
  const variantClasses = {
    default: 'text-subtle hover:bg-brand/5',
    primary: 'text-primary hover:bg-brand/5',
    danger: 'text-red-600 hover:bg-brand/5',
    ghost: 'text-brand hover:bg-brand/5',
  };

  const baseButtonClasses = `
    inline-flex items-center justify-center size-9
    text-sm font-medium rounded-lg
    focus:outline-none
    disabled:opacity-50 disabled:pointer-events-none
    cursor-pointer
    ${variantClasses[variant]}
  `.trim().replace(/\s+/g, ' ');

  const buttonClasses = tooltip
    ? `${baseButtonClasses} hs-tooltip-toggle`
    : baseButtonClasses;

  const button = (
    <button
      type="button"
      className={buttonClasses}
      onClick={onClick}
      aria-label={ariaLabel || tooltip}
      disabled={disabled}
    >
      <Icon icon={icon} className="size-4" />
    </button>
  );

  // Wrap with tooltip if provided
  if (tooltip) {
    return (
      <Tooltip content={tooltip} placement="top">
        {button}
      </Tooltip>
    );
  }

  return button;
}

ActionIconButton.propTypes = {
  icon: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  tooltip: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'primary', 'danger', 'ghost']),
  ariaLabel: PropTypes.string,
  disabled: PropTypes.bool,
};
